"use client";
import { useRef, useState } from "react";
import st from "@/styles/modules/typography.module.scss";
import s from "./CiteTabs.module.scss";
import cn from "classnames";
import Image from "next/legacy/image";
import { TabButtons } from "@/components/shared/TabButtons";

type CiteTabsQuotes = {
  className?: string;
  quotes: {
    format: "apa" | "mla" | "chicago1" | "chicago2" | "harvard";
    text: string;
  }[];
};

const formats = [
  { slug: "apa", name: "APA 7" },
  { slug: "mla", name: "MLA 9" },
  { slug: "chicago1", name: "Chicago (N-B)" },
  { slug: "chicago2", name: "Chicago (A-D)" },
  { slug: "harvard", name: "Harvard" },
];

export const CiteTabs = ({ className = "", quotes }: CiteTabsQuotes) => {
  const [activeTab, setActiveTab] = useState<string | number>(formats[0].slug);

  const [copied, setCopied] = useState(false);

  const copyTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const activeText = quotes.find(({ format }) => format === activeTab)?.text || "";

  const onCopyBtnClick = () => {
    const plainText = activeText
      .replace(/<br\/>/gi, "\n")
      .replace(/<\/p><p([^>]*)>/gi, "\n")
      .replace(/(<([^>]+)>)/gi, "");

    const htmlText = activeText.replace(/(https:+[^\s|<]+[\w])/gi, '<a href="$1">$1</a>');

    if ("ClipboardItem" in window) {
      navigator.clipboard.write([
        new ClipboardItem({
          "text/plain": new Blob([plainText], {
            type: "text/plain",
          }),
          "text/html": new Blob([htmlText], {
            type: "text/html",
          }),
        }),
      ]);
    } else {
      // workaround for firefox
      const tempElement = document.createElement("div");
      tempElement.setAttribute("style", "position: fixed; top: 0; left: 0; color: transparent;");
      tempElement.innerHTML = htmlText;

      document.body.appendChild(tempElement);

      const range = document.createRange();
      range.selectNodeContents(tempElement);

      const selection = window.getSelection();
      selection!.removeAllRanges();
      selection!.addRange(range);
      window.customCopyInProgress = true;
      document.execCommand("copy");
      window.customCopyInProgress = false;
      selection!.removeAllRanges();
      document.body.removeChild(tempElement);
    }

    setCopied(true);
    // @ts-ignore
    clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => {
      setCopied(false);
      // @ts-ignore
      clearTimeout(copyTimerRef.current);
    }, 5000);
  };

  return (
    <div className={cn(s.citeTabs, className)}>
      <TabButtons
        items={formats.map(({ slug, name }) => ({
          tabKey: slug,
          button: name,
        }))}
        activeKey={activeTab}
        onChange={(value) => {
          // @ts-ignore
          clearTimeout(copyTimerRef.current);
          setCopied(false);
          setActiveTab(value);
        }}
        className={s.btns}
        breakpoint="none"
        variant="light"
      />

      <div className={cn(st.card, s.content)} dangerouslySetInnerHTML={{ __html: activeText }} />

      <div className={s.btnWrap}>
        <button type="button" className={s.clipboardBtn} onClick={onCopyBtnClick}>
          <Image
            src={copied ? "/img/copy-alt.svg" : "/img/copy.svg"}
            alt="Illustration"
            width={24}
            height={24}
          />
          {copied ? "Copied!" : "Copy to clipboard"}
        </button>
      </div>
    </div>
  );
};
